/**
 * Gün Sonu Veri Servisi
 *
 * Bu dosya, EndOfDayReport.tsx içindeki handleFilter (~518-1684) ve
 * handlePrintReceipt (~2841-3334) mantığının BAĞIMSIZ bir kopyasıdır.
 *
 * KRİTİK NOTLAR:
 * - EndOfDayReport.tsx canlıda aktif kullanıldığı için BU GÖREVDE DEĞİŞTİRİLMEDİ.
 * - Bu servis, aynı API çağrılarını aynı sırada, aynı parametrelerle yapar.
 * - State setter'ları yerine pure async fonksiyonlar olarak çalışır.
 * - Çıktı (`EndOfDayPrintData`), `EndOfDayReport.tsx`'in ürettiği ile birebir aynı olur.
 *
 * Eğer EndOfDayReport.tsx'teki mantıkta bir değişiklik yapılırsa,
 * bu dosyada da aynı değişikliği yapmayı UNUTMA (single source of truth değil).
 */

import {
  restaurantActiveSaleApi,
  restaurantCompletedSaleApi,
  RestaurantActiveSaleDto,
  RestaurantCompletedSaleDto,
} from '../../../services/restaurantApiService';
import salesService, { QuickSaleReceiptDto } from '../../../services/salesService';
import { printEndOfDayReceipt, EndOfDayPrintData } from '../components/EndOfDayPrintService';
import {
  getProductSalesSummaryByInvoiceIds,
  getProductSalesSummaryByInvoiceNumbers,
  ProductSalesSummaryItem,
} from '../../../services/productSalesSummaryApi';
import { salesApi, getirApi, migrosApi, yemeksepetiApi, trendyolApi } from '../../../config/apiClient';
import {
  TransactionStatus,
  PaymentType,
  EndOfDaySummary,
  EndOfDayRestaurantRow,
  EndOfDayQuickSaleRow,
  EndOfDayKioskRow,
  EndOfDayQRMenuRow,
  EndOfDayPackageOrderRow,
  ScreenOption,
  PlatformOrderSummary,
  ReconciliationResult,
} from '../endOfDayReportTypes';

// ============================================================
// PUBLIC TYPES
// ============================================================

export interface EndOfDayPrintFilters {
  startDate: Date;
  endDate: Date;
  /** Boş dizi = TÜM ŞUBELER (aynı isAllBranches mantığı) */
  branchIds: string[];
  /** ÖKC filtresi: false (varsayılan) = ÖKC'siz nakit kayıtları GİZLE; true = TÜMÜ */
  filterFiscalOnly: boolean;
}

export interface EndOfDayServiceContext {
  /** Tüm şubeler (AuthContext'ten) */
  branches: Array<{ id: string; name: string }>;
  /** Şube ID'sinden şube adı çözer (AuthContext.getBranchName) */
  getBranchName: (branchId: string) => string;
  /** Tenant company ID */
  companyId: string;
  /** Manager modu mu? (true ise sadece kendi şubesini görür) */
  isManager?: boolean;
  /** Manager'in kilitli şubesi */
  managerBranchId?: string;
}

export interface EndOfDayDataset {
  restaurantData: EndOfDayRestaurantRow[];
  quickSaleData: EndOfDayQuickSaleRow[];
  kioskData: EndOfDayKioskRow[];
  qrMenuData: EndOfDayQRMenuRow[];
  packageOrderData: EndOfDayPackageOrderRow[];
  getirOrderData: EndOfDayPackageOrderRow[];
  migrosOrderData: EndOfDayPackageOrderRow[];
  ysOrderData: EndOfDayPackageOrderRow[];
  trendyolOrderData: EndOfDayPackageOrderRow[];
  reconciliationData: Record<string, ReconciliationResult>;
  summary: EndOfDaySummary | null;
  completedSales: RestaurantCompletedSaleDto[];
  activeTables: RestaurantActiveSaleDto[];
  /** İç hesaplama için kullanılan ekran listesi */
  screenOptions: ScreenOption[];
}

// ============================================================
// SABİT
// ============================================================

const emptyReconciliation: ReconciliationResult = {
  paid: [],
  unpaid: [],
  cancelled: [],
  warnings: [],
  paidTotal: 0,
  unpaidTotal: 0,
  cancelledTotal: 0,
  warningTotal: 0,
};

// ============================================================
// HELPER: combinedSummaryOld (handlePrintReceipt için summary)
// EndOfDayReport.tsx satır 1961-2145 ile birebir aynı.
// ============================================================

const calculateCombinedSummaryOld = (
  restaurantSales: RestaurantCompletedSaleDto[],
  quickSaleData: EndOfDayQuickSaleRow[],
  kioskData: EndOfDayKioskRow[],
  qrMenuData: EndOfDayQRMenuRow[],
  packageOrderData: EndOfDayPackageOrderRow[] = [],
  getirOrderData: EndOfDayPackageOrderRow[] = [],
  migrosOrderData: EndOfDayPackageOrderRow[] = [],
  ysOrderData: EndOfDayPackageOrderRow[] = [],
  trendyolOrderData: EndOfDayPackageOrderRow[] = []
): EndOfDaySummary => {
  const summary: EndOfDaySummary = {
    totalAmount: 0,
    totalCount: 0,
    cashAmount: 0,
    cashCount: 0,
    posAmount: 0,
    posCount: 0,
    otherAmount: 0,
    otherCount: 0,
    cancelledAmount: 0,
    cancelledCount: 0,
    mixedAmount: 0,
    mixedCount: 0,
    openAccountAmount: 0,
    openAccountCount: 0,
    posRefundAmount: 0,
    posRefundCount: 0,
    prepaidAmount: 0,
    prepaidCount: 0,
  };

  restaurantSales.forEach(sale => {
    const paymentMethod = sale.PaymentMethod?.toLowerCase() || '';
    const totalAmount = sale.TotalAmount || 0;

    if (paymentMethod === 'unpaid' || paymentMethod === 'cancelled' || paymentMethod === 'iptal') {
      summary.cancelledAmount += totalAmount;
      summary.cancelledCount += 1;
    } else {
      summary.totalAmount += totalAmount;
      summary.totalCount += 1;

      if (paymentMethod === 'nakit' || paymentMethod === 'cash') {
        summary.cashAmount += totalAmount;
        summary.cashCount += 1;
      } else if (paymentMethod === 'pos' || paymentMethod === 'kredi kartı' || paymentMethod === 'credit_card') {
        summary.posAmount += totalAmount;
        summary.posCount += 1;
      } else if (paymentMethod.includes('parçalı') || paymentMethod === 'partial' || paymentMethod.includes('karma') || paymentMethod.includes('mixed')) {
        summary.mixedAmount += totalAmount;
        summary.mixedCount += 1;
      } else if (paymentMethod === 'açık hesap' || paymentMethod === 'open_account') {
        summary.openAccountAmount += totalAmount;
        summary.openAccountCount += 1;
      } else {
        summary.otherAmount += totalAmount;
        summary.otherCount += 1;
      }
    }
  });

  quickSaleData.forEach(row => {
    if (row.isRefund && row.paidAmount < 0) {
      summary.posRefundAmount += Math.abs(row.paidAmount);
      summary.posRefundCount += 1;
      summary.totalAmount += row.paidAmount;
      return;
    }
    if (row.status === 'CANCELLED') {
      summary.cancelledAmount += row.grandTotal || 0;
      summary.cancelledCount += 1;
    } else {
      summary.totalAmount += row.paidAmount || 0;
      summary.totalCount += 1;

      if (row.payments && row.payments.length > 0) {
        row.payments.forEach(payment => {
          const method = payment.paymentMethod?.toLowerCase() || '';
          if (method === 'prepaid') {
            summary.prepaidAmount += payment.amount || 0;
          } else if (method === 'open_account') {
            summary.openAccountAmount += payment.amount || 0;
          } else if (method === 'online_online' || (method.startsWith('online_') && method.endsWith('_online'))) {
            summary.otherAmount += payment.amount || 0;
          } else if (method === 'cash' || method === 'nakit' || method === 'online_cash') {
            summary.cashAmount += payment.amount || 0;
          } else if (method === 'pos' || method === 'credit_card' || method === 'online_pos') {
            summary.posAmount += payment.amount || 0;
          } else if (method) {
            summary.otherAmount += payment.amount || 0;
          }
        });
        if (row.payments.length > 1) {
          summary.mixedCount += 1;
        } else {
          const method = row.payments[0]?.paymentMethod?.toLowerCase() || '';
          if (method === 'prepaid') {
            summary.prepaidCount += 1;
          } else if (method === 'open_account') {
            summary.openAccountCount += 1;
          } else if (method === 'online_online' || (method.startsWith('online_') && method.endsWith('_online'))) {
            summary.otherCount += 1;
          } else if (method === 'cash' || method === 'nakit' || method === 'online_cash') {
            summary.cashCount += 1;
          } else if (method === 'pos' || method === 'credit_card' || method === 'online_pos') {
            summary.posCount += 1;
          } else {
            summary.otherCount += 1;
          }
        }
      } else {
        if (row.paymentType === 'PREPAID') {
          summary.prepaidAmount += row.paidAmount || 0;
          summary.prepaidCount += 1;
        } else if (row.paymentType === 'OPEN_ACCOUNT') {
          summary.openAccountAmount += row.paidAmount || 0;
          summary.openAccountCount += 1;
        } else if (row.paymentType === 'ONLINE') {
          summary.otherAmount += row.paidAmount || 0;
          summary.otherCount += 1;
        } else if (row.paymentType === 'CASH') {
          summary.cashAmount += row.paidAmount || 0;
          summary.cashCount += 1;
        } else if (row.paymentType === 'POS') {
          summary.posAmount += row.paidAmount || 0;
          summary.posCount += 1;
        } else if (row.paymentType === 'MIXED') {
          summary.mixedAmount += row.paidAmount || 0;
          summary.mixedCount += 1;
        }
      }
    }
  });

  const processGenericSimple = (data: any[]) => {
    data.forEach(row => {
      if (row.status === 'CANCELLED') {
        summary.cancelledAmount += row.grandTotal || 0;
        summary.cancelledCount += 1;
      } else {
        const amount = row.paidAmount || 0;
        summary.totalAmount += amount;
        summary.totalCount += 1;
        if (row.paymentType === 'OPEN_ACCOUNT') { summary.openAccountAmount += amount; summary.openAccountCount += 1; }
        else if (row.paymentType === 'ONLINE') { summary.otherAmount += amount; summary.otherCount += 1; }
        else if (row.paymentType === 'CASH') { summary.cashAmount += amount; summary.cashCount += 1; }
        else if (row.paymentType === 'POS') { summary.posAmount += amount; summary.posCount += 1; }
        else if (row.paymentType === 'MIXED') { summary.mixedAmount += amount; summary.mixedCount += 1; }
        else if (row.paymentType === 'OTHER') { summary.otherAmount += amount; summary.otherCount += 1; }
      }
    });
  };

  processGenericSimple(kioskData);
  processGenericSimple(qrMenuData);

  const processPlatformOrdersOld = (data: EndOfDayPackageOrderRow[]) => {
    data.forEach(row => {
      if (row.status === 'CANCELLED') {
        summary.cancelledAmount += row.grandTotal || 0;
        summary.cancelledCount += 1;
      } else if (row.status === 'UNPAID') {
        const amount = row.grandTotal || 0;
        summary.totalAmount += amount;
        summary.totalCount += 1;
        summary.otherAmount += amount;
        summary.otherCount += 1;
      } else if (row.status === 'PAID' || row.status === 'PARTIAL') {
        const amount = row.paidAmount || 0;
        summary.totalAmount += amount;
        summary.totalCount += 1;
        if (row.paymentType === 'ONLINE') { summary.otherAmount += amount; summary.otherCount += 1; }
        else if (row.paymentType === 'CASH') { summary.cashAmount += amount; summary.cashCount += 1; }
        else if (row.paymentType === 'POS') { summary.posAmount += amount; summary.posCount += 1; }
        else if (row.paymentType === 'MIXED') { summary.mixedAmount += amount; summary.mixedCount += 1; }
        else if (row.paymentType === 'OTHER') { summary.otherAmount += amount; summary.otherCount += 1; }
        else { summary.otherAmount += amount; summary.otherCount += 1; }
      }
    });
  };
  processPlatformOrdersOld(packageOrderData);
  processPlatformOrdersOld(getirOrderData);
  processPlatformOrdersOld(migrosOrderData);
  processPlatformOrdersOld(ysOrderData);
  processPlatformOrdersOld(trendyolOrderData);

  return summary;
};

// ============================================================
// MAIN: fetchEndOfDayDataset
// EndOfDayReport.tsx handleFilter (518-1684) ile birebir aynı mantık.
// ============================================================

export async function fetchEndOfDayDataset(
  filters: EndOfDayPrintFilters,
  ctx: EndOfDayServiceContext,
  /** Opsiyonel: zaten yüklü ekran listesi varsa pas geç (tasarruf için) */
  preloadedScreenOptions?: ScreenOption[]
): Promise<EndOfDayDataset> {
  const { startDate, endDate, branchIds: selectedBranches } = filters;
  const { companyId, getBranchName } = ctx;

  if (!startDate || !endDate) {
    throw new Error('Başlangıç ve bitiş tarihi gereklidir');
  }
  if (!companyId) {
    throw new Error('Şirket bilgisi bulunamadı');
  }

  const isAllBranches = selectedBranches.length === 0;

  // Ekran listesini al
  let screenOptions: ScreenOption[] = preloadedScreenOptions ?? [];
  if (screenOptions.length === 0) {
    try {
      const { restaurantScreenApi } = await import('../../../services/restaurantApiService');
      const screens = await restaurantScreenApi.getByCompanyId();
      screenOptions = screens.map(s => ({
        id: s.Id,
        name: s.Name,
        type: 'RESTAURANT' as const,
        branchId: s.BranchId || null,
      }));
    } catch (e) {
      console.warn('[GÜN SONU SERVICE] Ekran listesi yüklenemedi, devam ediliyor:', e);
    }
  }

  console.log('[GÜN SONU SERVICE] 🔄 Tüm satış kanalları filtreleniyor...', {
    companyId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    selectedBranches,
    isAllBranches,
    screenOptions: screenOptions.map(s => ({ id: s.id, name: s.name, type: s.type, branchId: s.branchId })),
  });

  // Şube seçimine göre restoran ekranlarını belirle
  let restaurantScreensToFetch: ScreenOption[] = [];
  if (!isAllBranches) {
    restaurantScreensToFetch = screenOptions.filter(s => s.type === 'RESTAURANT' && s.branchId && selectedBranches.includes(s.branchId));
  } else {
    restaurantScreensToFetch = screenOptions.filter(s => s.type === 'RESTAURANT');
  }

  // PARALEL API ÇAĞRILARI
  const [restaurantResult, quickSaleResult, kioskResult, qrMenuResult, packageOrderResult] = await Promise.allSettled([
    // 1. RESTORAN
    (async () => {
      if (!isAllBranches) {
        if (restaurantScreensToFetch.length > 0) {
          const results = await Promise.allSettled(
            restaurantScreensToFetch.map(screen =>
              restaurantCompletedSaleApi.getByRestaurantScreenId(
                screen.id,
                startDate.toISOString(),
                endDate.toISOString()
              )
            )
          );
          return results.flatMap(r => r.status === 'fulfilled' ? r.value : []);
        }
        const branchResults = await Promise.allSettled(
          selectedBranches.map(branchId =>
            restaurantCompletedSaleApi.getByCompanyId(
              startDate.toISOString(),
              endDate.toISOString(),
              branchId
            )
          )
        );
        return branchResults.flatMap(r => r.status === 'fulfilled' ? r.value : []);
      }
      return restaurantCompletedSaleApi.getByCompanyId(
        startDate.toISOString(),
        endDate.toISOString()
      );
    })(),
    // 2. HIZLI SATIŞ
    (async () => {
      if (!isAllBranches) {
        const qsResults = await Promise.allSettled(
          selectedBranches.map(branchId =>
            salesService.getQuickSaleReceiptsByDateRange(
              startDate.toISOString(),
              endDate.toISOString(),
              branchId
            )
          )
        );
        return qsResults.flatMap(r => r.status === 'fulfilled' ? r.value : []);
      }
      return salesService.getQuickSaleReceiptsByDateRange(
        startDate.toISOString(),
        endDate.toISOString(),
        null
      );
    })(),
    // 3. KIOSK
    Promise.resolve([] as EndOfDayKioskRow[]),
    // 4. QR MENÜ
    Promise.resolve([] as EndOfDayQRMenuRow[]),
    // 5. PAKET SİPARİŞ (artık QuickSaleReceipts'ten filtreleniyor)
    Promise.resolve({ Success: true, Data: [], Count: 0 }),
  ]);

  // ✅ RESTORAN VERİLERİNİ İŞLE
  let restaurantSales: RestaurantCompletedSaleDto[] = [];
  let processedRestaurantData: EndOfDayRestaurantRow[] = [];
  if (restaurantResult.status === 'fulfilled') {
    restaurantSales = restaurantResult.value;

    // Güvenlik filtresi: Şube seçiliyse, sadece o şubelerin satışlarını göster
    if (!isAllBranches) {
      const branchScreenIds = new Set(restaurantScreensToFetch.map(s => s.id));
      restaurantSales = restaurantSales.filter(sale =>
        (sale.BranchId && selectedBranches.includes(sale.BranchId)) || branchScreenIds.has(sale.RestaurantScreenId)
      );
    }

    const transformedData: EndOfDayRestaurantRow[] = restaurantSales.map((sale, index) => {
      const paymentMethodLower = sale.PaymentMethod?.toLowerCase() || '';
      let status: TransactionStatus = 'PAID';
      let paymentType: PaymentType = 'CASH';

      if (paymentMethodLower === 'unpaid' || paymentMethodLower === 'cancelled' || paymentMethodLower === 'iptal') {
        status = 'CANCELLED';
        paymentType = 'NONE';
      } else if (paymentMethodLower.includes('parçalı') || paymentMethodLower.includes('karma') || paymentMethodLower === 'partial' || paymentMethodLower.includes('mixed')) {
        status = 'PAID';
        paymentType = 'MIXED';
      } else if (paymentMethodLower === 'pos' || paymentMethodLower === 'kredi kartı' || paymentMethodLower === 'credit_card') {
        paymentType = 'POS';
      } else if (paymentMethodLower === 'nakit' || paymentMethodLower === 'cash') {
        paymentType = 'CASH';
      } else if (paymentMethodLower === 'açık hesap' || paymentMethodLower === 'open_account') {
        paymentType = 'OPEN_ACCOUNT';
      }

      const saleScreen = screenOptions.find(s => s.id === sale.RestaurantScreenId);
      const screenBranchName = saleScreen?.branchId ? getBranchName(saleScreen.branchId) : '-';

      return {
        id: String(sale.Id),
        rowNumber: index + 1,
        tableName: sale.TableName || `Masa ${sale.TableId}`,
        orderNumber: sale.SaleNumber || '',
        status,
        subtotal: sale.SubTotal || 0,
        grandTotal: sale.TotalAmount || 0,
        paidAmount: status === 'CANCELLED' ? 0 : sale.TotalAmount || 0,
        paymentType,
        cancelledTotal: status === 'CANCELLED' ? sale.TotalAmount || 0 : 0,
        openedAt: sale.OpenedAt,
        closedAt: sale.CompletedAt,
        seatingDuration: sale.DurationMinutes || 0,
        notes: sale.Notes || '',
        receiptNumber: sale.ReservedShortCode || '',
        customerName: sale.InvoiceNumber ? 'Genel Cari' : '',
        waiterName: sale.ClosedByWaiterName || sale.CompletedByName || '',
        invoiceNumber: sale.InvoiceNumber || '',
        branchName: screenBranchName,
        screenName: saleScreen?.name || '-',
        invoiceId: (sale as any).InvoiceId || undefined,
        isFiscalReceipt: sale.IsFiscalReceipt ?? true,
      };
    });

    const sortedData = transformedData.sort((a, b) => {
      const dateA = new Date(a.openedAt).getTime();
      const dateB = new Date(b.openedAt).getTime();
      return dateB - dateA;
    });
    sortedData.forEach((row, index) => { row.rowNumber = index + 1; });
    processedRestaurantData = sortedData;
  }

  // ✅ HIZLI SATIŞ VERİLERİNİ İŞLE
  let processedQuickSales: EndOfDayQuickSaleRow[] = [];
  let processedPackageOrders: EndOfDayPackageOrderRow[] = [];
  let processedGetirOrders: EndOfDayPackageOrderRow[] = [];
  let processedMigrosOrders: EndOfDayPackageOrderRow[] = [];
  let processedYsOrders: EndOfDayPackageOrderRow[] = [];
  let processedTrendyolOrders: EndOfDayPackageOrderRow[] = [];
  let processedKioskData: EndOfDayKioskRow[] = [];

  if (quickSaleResult.status === 'fulfilled') {
    const receipts = quickSaleResult.value as QuickSaleReceiptDto[];

    const transformedQuickSales: EndOfDayQuickSaleRow[] = receipts.map((receipt, index) => {
      let paymentType: PaymentType = 'CASH';
      const paymentTypeLower = (receipt.PaymentType || '').toLowerCase();

      const hasOtherPaymentMethod = (receipt.Payments || []).some(
        (p: any) => p.PaymentMethod?.toLowerCase() === 'other'
      );

      if (hasOtherPaymentMethod) {
        paymentType = 'OTHER';
      } else if (receipt.PaymentType === 'OPEN_ACCOUNT' || paymentTypeLower === 'open_account') {
        paymentType = 'OPEN_ACCOUNT';
      } else if (paymentTypeLower === 'online_online') {
        paymentType = 'ONLINE';
      } else if (receipt.PaymentType === 'POS' || paymentTypeLower === 'online_pos' || paymentTypeLower.includes('_pos')) {
        paymentType = 'POS';
      } else if (receipt.PaymentType === 'PARTIAL') {
        paymentType = 'MIXED';
      } else if (paymentTypeLower === 'online_cash' || paymentTypeLower.startsWith('online_')) {
        paymentType = 'CASH';
      }

      let status: TransactionStatus = 'PAID';
      let isRefund = false;
      if (receipt.PaymentStatus === 'UNPAID') {
        status = 'UNPAID';
      } else if (receipt.PaymentStatus === 'CANCELLED') {
        status = 'CANCELLED';
        paymentType = 'NONE';
      } else if (receipt.PaymentStatus === 'REFUNDED') {
        status = 'CANCELLED';
        isRefund = true;
      }

      const branchName = receipt.BranchId ? getBranchName(receipt.BranchId) : '-';

      let paymentAccountName = '-';
      const payments = receipt.Payments || [];

      const getFullBankName = (p: any): string => {
        if (p.BankAccountName) {
          if (p.BankSubAccountName) {
            return `${p.BankAccountName} - ${p.BankSubAccountName}`;
          }
          return p.BankAccountName;
        }
        return '';
      };

      if (payments.length > 0) {
        if (payments.length === 1) {
          const p = payments[0];
          paymentAccountName = p.CashboxName || getFullBankName(p) || '-';
        } else {
          const names: string[] = [];
          payments.forEach(p => {
            if (p.CashboxName) names.push(p.CashboxName);
            const bankName = getFullBankName(p);
            if (bankName) names.push(bankName);
          });
          paymentAccountName = names.length > 0 ? names.join(' + ') : '-';
        }
      }

      let paymentProviderName: string | undefined = undefined;
      if (payments.length > 0) {
        const firstProviderName = payments.find(p => p.PaymentProviderName)?.PaymentProviderName;
        if (firstProviderName) {
          paymentProviderName = firstProviderName;
        }
      }

      return {
        id: receipt.Id.toString(),
        rowNumber: index + 1,
        referenceNumber: receipt.ReceiptNumber || receipt.ReferenceNumber || '',
        status,
        subtotal: receipt.SubTotal,
        grandTotal: receipt.TotalAmount,
        paidAmount: status === 'CANCELLED' ? 0 : receipt.TotalPaid,
        paymentType,
        paymentAccountName,
        cancelledTotal: status === 'CANCELLED' ? receipt.TotalAmount : 0,
        transactionDate: receipt.CreatedAt,
        openedAt: receipt.OpenedAt || receipt.CreatedAt,
        notes: receipt.Notes || '',
        receiptNumber: receipt.ShortCode || '',
        customerName: receipt.AccountName || 'Genel Cari',
        invoiceNumber: receipt.InvoiceNumber || '',
        branchName: branchName,
        createdByName: receipt.CashierName || receipt.CreatedByName || '',
        cashierName: receipt.CashierName || undefined,
        paymentProviderName,
        payments: payments.map(p => ({
          paymentMethod: p.PaymentMethod,
          amount: p.Amount,
          cashboxName: p.CashboxName,
          bankAccountName: p.BankAccountName,
          bankSubAccountName: p.BankSubAccountName,
          paymentProviderName: p.PaymentProviderName || undefined,
          paymentProviderType: p.PaymentProviderType || undefined,
        })),
        invoiceId: receipt.InvoiceId || undefined,
        isFiscalReceipt: (receipt as any).IsFiscalReceipt ?? true,
        isRefund,
        branchId: receipt.BranchId != null && String(receipt.BranchId).trim() !== ''
          ? String(receipt.BranchId).trim()
          : undefined,
      };
    });

    let scopedQuickSaleRows = transformedQuickSales;
    if (!isAllBranches) {
      scopedQuickSaleRows = transformedQuickSales.filter(row => {
        const bid = row.branchId ? String(row.branchId).trim() : '';
        return bid !== '' && selectedBranches.includes(bid);
      });
    }

    const isKioskReceipt = (row: EndOfDayQuickSaleRow) =>
      row.referenceNumber?.startsWith('D-') || row.referenceNumber?.startsWith('P-') ||
      row.receiptNumber?.startsWith('D-') || row.receiptNumber?.startsWith('P-');

    const kioskSales = scopedQuickSaleRows.filter(row => isKioskReceipt(row));
    const packageSales = scopedQuickSaleRows.filter(row =>
      row.referenceNumber?.startsWith('PKT-') || row.receiptNumber?.startsWith('PKT-')
    );
    const getirSales = scopedQuickSaleRows.filter(row =>
      row.referenceNumber?.startsWith('GTR-') || row.receiptNumber?.startsWith('GTR-')
    );
    const migrosSales = scopedQuickSaleRows.filter(row =>
      row.referenceNumber?.startsWith('MGR-') || row.receiptNumber?.startsWith('MGR-')
    );
    const ysSales = scopedQuickSaleRows.filter(row =>
      row.referenceNumber?.startsWith('YS-') || row.receiptNumber?.startsWith('YS-')
    );
    const trdSales = scopedQuickSaleRows.filter(row =>
      row.referenceNumber?.startsWith('TRD-') || row.receiptNumber?.startsWith('TRD-')
    );
    const regularQuickSales = scopedQuickSaleRows.filter(row =>
      !isKioskReceipt(row) &&
      !row.referenceNumber?.startsWith('PKT-') && !row.receiptNumber?.startsWith('PKT-') &&
      !row.referenceNumber?.startsWith('GTR-') && !row.receiptNumber?.startsWith('GTR-') &&
      !row.referenceNumber?.startsWith('MGR-') && !row.receiptNumber?.startsWith('MGR-') &&
      !row.referenceNumber?.startsWith('YS-') && !row.receiptNumber?.startsWith('YS-') &&
      !row.referenceNumber?.startsWith('TRD-') && !row.receiptNumber?.startsWith('TRD-')
    );

    const sortedQuickSales = regularQuickSales.sort((a, b) => {
      const dateA = new Date(a.transactionDate).getTime();
      const dateB = new Date(b.transactionDate).getTime();
      return dateB - dateA;
    });
    sortedQuickSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedQuickSales = sortedQuickSales;

    // PAKET
    const sortedPackageSales = packageSales.sort((a, b) => {
      const dateA = new Date(a.transactionDate).getTime();
      const dateB = new Date(b.transactionDate).getTime();
      return dateB - dateA;
    });
    sortedPackageSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedPackageOrders = sortedPackageSales.map((row) => ({
      id: row.id,
      rowNumber: row.rowNumber,
      orderNumber: row.referenceNumber || '',
      shortCode: row.receiptNumber || '',
      status: row.status,
      orderStatus: 'DELIVERED',
      subtotal: row.subtotal,
      grandTotal: row.grandTotal,
      paidAmount: row.paidAmount,
      paymentType: row.paymentType,
      paymentAccountName: row.paymentAccountName,
      cancelledTotal: row.cancelledTotal,
      createdAt: row.openedAt || row.transactionDate,
      paymentDate: row.transactionDate,
      customerName: row.customerName,
      customerPhone: '',
      customerAddress: '',
      courierName: '',
      invoiceNumber: row.invoiceNumber,
      branchName: row.branchName,
      branchId: row.branchId ?? undefined,
      cashierName: row.cashierName || row.createdByName,
      invoiceId: row.invoiceId,
      paymentProviderName: row.paymentProviderName,
      cashAmount: row.paymentType === 'CASH' ? row.paidAmount : 0,
      posAmount: row.paymentType === 'POS' ? row.paidAmount : 0,
      otherAmount: row.paymentType === 'OTHER' ? row.paidAmount : 0,
    }));

    // GETIR
    const sortedGetirSales = getirSales.sort((a, b) => {
      const dateA = new Date(a.transactionDate).getTime();
      const dateB = new Date(b.transactionDate).getTime();
      return dateB - dateA;
    });
    sortedGetirSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedGetirOrders = sortedGetirSales.map((row) => ({
      id: row.id,
      rowNumber: row.rowNumber,
      orderNumber: row.referenceNumber || '',
      shortCode: row.receiptNumber || '',
      status: row.status,
      orderStatus: 'DELIVERED',
      subtotal: row.subtotal,
      grandTotal: row.grandTotal,
      paidAmount: row.paidAmount,
      paymentType: row.paymentType,
      paymentAccountName: row.paymentAccountName,
      cancelledTotal: row.cancelledTotal,
      createdAt: row.openedAt || row.transactionDate,
      paymentDate: row.transactionDate,
      customerName: row.customerName,
      customerPhone: '',
      customerAddress: '',
      courierName: '',
      invoiceNumber: row.invoiceNumber,
      branchName: row.branchName,
      branchId: row.branchId ?? undefined,
      cashierName: row.cashierName || row.createdByName,
      invoiceId: row.invoiceId,
      paymentProviderName: row.paymentProviderName,
      cashAmount: row.paymentType === 'CASH' ? row.paidAmount : 0,
      posAmount: row.paymentType === 'POS' ? row.paidAmount : 0,
      otherAmount: row.paymentType === 'OTHER' ? row.paidAmount : 0,
    }));

    // MIGROS
    const sortedMigrosSales = migrosSales.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
    sortedMigrosSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedMigrosOrders = sortedMigrosSales.map((row) => ({
      id: row.id, rowNumber: row.rowNumber,
      orderNumber: row.referenceNumber || '', shortCode: row.receiptNumber || '',
      status: row.status, orderStatus: 'DELIVERED',
      subtotal: row.subtotal, grandTotal: row.grandTotal, paidAmount: row.paidAmount,
      paymentType: row.paymentType, paymentAccountName: row.paymentAccountName,
      cancelledTotal: row.cancelledTotal, createdAt: row.openedAt || row.transactionDate, paymentDate: row.transactionDate,
      customerName: row.customerName, customerPhone: '', customerAddress: '', courierName: '',
      invoiceNumber: row.invoiceNumber, branchName: row.branchName,
      branchId: row.branchId ?? undefined,
      cashierName: row.cashierName || row.createdByName, invoiceId: row.invoiceId,
      paymentProviderName: row.paymentProviderName,
      cashAmount: row.paymentType === 'CASH' ? row.paidAmount : 0,
      posAmount: row.paymentType === 'POS' ? row.paidAmount : 0,
      otherAmount: row.paymentType === 'OTHER' ? row.paidAmount : 0,
    }));

    // YS
    const sortedYsSales = ysSales.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
    sortedYsSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedYsOrders = sortedYsSales.map((row) => ({
      id: row.id, rowNumber: row.rowNumber,
      orderNumber: row.referenceNumber || '', shortCode: row.receiptNumber || '',
      status: row.status, orderStatus: 'DELIVERED',
      subtotal: row.subtotal, grandTotal: row.grandTotal, paidAmount: row.paidAmount,
      paymentType: row.paymentType, paymentAccountName: row.paymentAccountName,
      cancelledTotal: row.cancelledTotal, createdAt: row.openedAt || row.transactionDate, paymentDate: row.transactionDate,
      customerName: row.customerName, customerPhone: '', customerAddress: '', courierName: '',
      invoiceNumber: row.invoiceNumber, branchName: row.branchName,
      branchId: row.branchId ?? undefined,
      cashierName: row.cashierName || row.createdByName, invoiceId: row.invoiceId,
      paymentProviderName: row.paymentProviderName,
      cashAmount: row.paymentType === 'CASH' ? row.paidAmount : 0,
      posAmount: row.paymentType === 'POS' ? row.paidAmount : 0,
      otherAmount: row.paymentType === 'OTHER' ? row.paidAmount : 0,
      // Yemeksepeti v2026 (15.05.2026) sponsorluk dokumu — altyapi rezervi.
      // PaySalesService bu alanlari henuz doldurmuyor; backend ileride
      // YemeksepetiOrders.OrderDiscountsJson'i parse edip platform/vendor/3rd-party
      // toplamlarini buraya gonderdiginde EndOfDayReport tek satirlik degisiklikle
      // sponsorluk ozetini gosterebilecek. Simdilik undefined birakiyoruz —
      // varolan satirlar/raporlar etkilenmiyor.
      ysPlatformDiscountAmount: undefined,
      ysVendorDiscountAmount: undefined,
      ysThirdPartyDiscountAmount: undefined,
      ysTotalDiscountAmount: undefined,
    }));

    // TRENDYOL
    const sortedTrdSales = trdSales.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
    sortedTrdSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedTrendyolOrders = sortedTrdSales.map((row) => ({
      id: row.id, rowNumber: row.rowNumber,
      orderNumber: row.referenceNumber || '', shortCode: row.receiptNumber || '',
      status: row.status, orderStatus: 'DELIVERED',
      subtotal: row.subtotal, grandTotal: row.grandTotal, paidAmount: row.paidAmount,
      paymentType: row.paymentType, paymentAccountName: row.paymentAccountName,
      cancelledTotal: row.cancelledTotal, createdAt: row.openedAt || row.transactionDate, paymentDate: row.transactionDate,
      customerName: row.customerName, customerPhone: '', customerAddress: '', courierName: '',
      invoiceNumber: row.invoiceNumber, branchName: row.branchName,
      branchId: row.branchId ?? undefined,
      cashierName: row.cashierName || row.createdByName, invoiceId: row.invoiceId,
      paymentProviderName: row.paymentProviderName,
      cashAmount: row.paymentType === 'CASH' ? row.paidAmount : 0,
      posAmount: row.paymentType === 'POS' ? row.paidAmount : 0,
      otherAmount: row.paymentType === 'OTHER' ? row.paidAmount : 0,
    }));

    // KIOSK
    const sortedKioskSales = kioskSales.sort((a, b) => {
      const dateA = new Date(a.transactionDate).getTime();
      const dateB = new Date(b.transactionDate).getTime();
      return dateB - dateA;
    });
    sortedKioskSales.forEach((row, index) => { row.rowNumber = index + 1; });
    processedKioskData = sortedKioskSales.map((row) => ({
      id: row.id,
      rowNumber: row.rowNumber,
      referenceNumber: row.referenceNumber || row.receiptNumber || '',
      status: row.status,
      subtotal: row.subtotal,
      grandTotal: row.grandTotal,
      paidAmount: row.paidAmount,
      paymentType: row.paymentType,
      cancelledTotal: row.cancelledTotal,
      transactionDate: row.transactionDate,
      notes: row.notes || '',
      customerName: row.customerName || 'Kiosk Müşterisi',
      kioskName: row.referenceNumber?.startsWith('D-') ? 'Burada Ye' : 'Paket',
      invoiceId: row.invoiceId,
      invoiceNumber: row.invoiceNumber || '',
      paymentAccountName: row.paymentAccountName,
      payments: row.payments,
    }));
  }

  // QR Menü
  let processedQRMenuData: EndOfDayQRMenuRow[] = [];
  if (qrMenuResult.status === 'fulfilled') {
    processedQRMenuData = qrMenuResult.value;
  }

  // Açık masaları çek (sadece restoran için)
  const activeResults = await Promise.allSettled(
    restaurantScreensToFetch.map(screen =>
      restaurantActiveSaleApi.getByRestaurantScreenId(screen.id)
    )
  );
  const allActiveSales = activeResults.flatMap(r => r.status === 'fulfilled' ? r.value : []);

  // ============================================================
  // SAĞLAMA SİSTEMİ
  // ============================================================
  const branchParam = !isAllBranches ? selectedBranches[0] : '';
  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  const buildReconciliationParams = (extra?: Record<string, string>) => {
    const p = new URLSearchParams();
    p.append('page', '1');
    p.append('pageSize', '5000');
    p.append('startDate', startISO);
    p.append('endDate', endISO);
    if (branchParam) p.append('branchId', branchParam);
    if (extra) Object.entries(extra).forEach(([k, v]) => p.append(k, v));
    return p.toString();
  };

  const fetchTrendyolPlatformMerged = async (): Promise<PromiseSettledResult<any>> => {
    try {
      if (isAllBranches) {
        const r = await trendyolApi.get(`/api/trendyol/order/list?${buildReconciliationParams()}`);
        return { status: 'fulfilled', value: r.data };
      }
      const merged: any[] = [];
      const seen = new Set<string>();
      for (const bid of selectedBranches) {
        const p = new URLSearchParams();
        p.append('page', '1');
        p.append('pageSize', '5000');
        p.append('startDate', startISO);
        p.append('endDate', endISO);
        p.append('branchId', bid);
        const r = await trendyolApi.get(`/api/trendyol/order/list?${p.toString()}`);
        const raw = r.data?.Data ?? r.data?.data ?? [];
        for (const o of raw) {
          const oid = String(o.id ?? o.Id ?? '');
          if (!oid || seen.has(oid)) continue;
          seen.add(oid);
          merged.push(o);
        }
      }
      return { status: 'fulfilled', value: { Success: true, Data: merged } };
    } catch (e) {
      return { status: 'rejected', reason: e };
    }
  };

  const [pkgPlatform, getirPlatform, migrosPlatform, ysPlatform] = await Promise.allSettled([
    salesApi.get(`/api/PackageOrder/by-date-range?${buildReconciliationParams()}`).then(r => r.data).catch(() => null),
    getirApi.get(`/api/getir-orders/list?${buildReconciliationParams()}`).then(r => r.data).catch(() => null),
    migrosApi.get(`/api/migros-orders/list?${buildReconciliationParams()}`).then(r => r.data).catch(() => null),
    yemeksepetiApi.get(`/api/ys-orders/list?${buildReconciliationParams()}`).then(r => r.data).catch(() => null),
  ]);
  const trendyolPlatform = await fetchTrendyolPlatformMerged();

  const filterStartMs = startDate.getTime();
  const filterEndMs = endDate.getTime();

  const restaurantAsRows = restaurantSales.map(s => ({
    invoiceNumber: s.InvoiceNumber || '',
    invoiceId: '',
    grandTotal: s.TotalAmount || 0,
  }));
  const allReceipts = [...processedQuickSales, ...processedKioskData, ...processedPackageOrders, ...processedGetirOrders, ...processedMigrosOrders, ...processedYsOrders, ...processedTrendyolOrders, ...restaurantAsRows];

  const invoiceNumberSet = new Set<string>();
  const invoiceIdSet = new Set<string>();
  const invoiceIdToNumber = new Map<string, string>();
  const invoiceIdToAmount = new Map<string, number>();
  for (const r of allReceipts) {
    const invNum = (r as any).invoiceNumber ?? (r as any).InvoiceNumber ?? '';
    const invId = (r as any).invoiceId ?? (r as any).InvoiceId ?? '';
    const amount = (r as any).grandTotal ?? (r as any).paidAmount ?? 0;
    if (invNum) invoiceNumberSet.add(invNum);
    if (invId) invoiceIdSet.add(invId);
    if (invId && invNum) invoiceIdToNumber.set(invId, invNum);
    if (invId) invoiceIdToAmount.set(invId, amount);
  }

  const filterByDate = (orders: any[]): any[] =>
    orders.filter(o => {
      const raw =
        o.orderDate ?? o.OrderDate ??
        o.createdAt ?? o.CreatedAt ?? o.receivedAt ?? o.ReceivedAt ?? '';
      if (!raw) return true;
      const t = new Date(raw).getTime();
      return !isNaN(t) && t >= filterStartMs && t <= filterEndMs;
    });

  const extractCustomerName = (order: any): string => {
    if (order.CustomerName || order.customerName) {
      return String(order.CustomerName ?? order.customerName);
    }
    if (order.customer && (order.customer.firstName || order.customer.lastName)) {
      return [order.customer.firstName, order.customer.lastName].filter(Boolean).join(' ').trim();
    }
    if (order.OrderDetailsJson || order.orderDetailsJson) {
      try {
        const json = typeof (order.OrderDetailsJson ?? order.orderDetailsJson) === 'string'
          ? JSON.parse(order.OrderDetailsJson ?? order.orderDetailsJson) : (order.OrderDetailsJson ?? order.orderDetailsJson);
        if (json?.customer) {
          return [json.customer.firstName, json.customer.lastName].filter(Boolean).join(' ').trim();
        }
      } catch {}
    }
    return order.clientName ?? order.ClientName
      ?? order.customerFullName ?? order.CustomerFullName
      ?? order.customerFirstName ?? order.CustomerFirstName
      ?? '';
  };

  const reconcile = (platformOrders: any[], platform: string, receiptPrefix: string, amountDivisor: number = 1): ReconciliationResult => {
    const paid: PlatformOrderSummary[] = [];
    const unpaid: PlatformOrderSummary[] = [];
    const cancelled: PlatformOrderSummary[] = [];
    const warnings: PlatformOrderSummary[] = [];

    const prefixReceipts = allReceipts.filter((r: any) =>
      r.invoiceNumber && ((r.referenceNumber ?? r.orderNumber ?? '')?.startsWith(receiptPrefix) || (r.receiptNumber ?? r.shortCode ?? '')?.startsWith(receiptPrefix))
    );
    const prefixInvoiceNumbers = new Set(prefixReceipts.map(r => r.invoiceNumber).filter(Boolean));

    for (const order of platformOrders) {
      const id = String(order.id ?? order.Id ?? '');
      const orderNumber = order.orderNumber ?? order.OrderNumber ?? order.confirmationId ?? order.ConfirmationId ?? order.shortCode ?? order.ShortCode ?? '';
      const customerName = extractCustomerName(order) || '-';
      // ⚠️ PLATFORM-SPESIFIK NET ONCELIGI (bkz. EndOfDayReport.tsx reconcile).
      // Sadece Getir icin NET'i (totalDiscountedPrice) oncelikli aliyoruz;
      // cunku Getir backend `totalAmount` alanini BRUT olarak dolduruyor.
      // Diger platformlar icin eski davranisa sadik kaliyoruz.
      let rawAmount: number;
      if (platform === 'getir') {
        rawAmount = Number(
          order.totalDiscountedPrice ?? order.TotalDiscountedPrice ??
          order.totalPrice ?? order.TotalPrice ?? 0
        );
      } else {
        rawAmount = Number(
          order.totalAmount ?? order.TotalAmount ??
          order.grandTotal ?? order.GrandTotal ??
          order.totalPrice ?? order.TotalPrice ??
          order.ToplamTutar ?? order.toplamTutar ?? 0
        );
      }
      const totalAmount = amountDivisor !== 1 ? Math.round((rawAmount / amountDivisor) * 100) / 100 : rawAmount;
      const createdAt =
        order.orderDate ?? order.OrderDate ??
        order.createdAt ?? order.CreatedAt ?? order.receivedAt ?? order.ReceivedAt ?? '';
      let invoiceNumber = order.invoiceNumber ?? order.InvoiceNumber ?? '';
      const invoiceId = order.invoiceId ?? order.InvoiceId ?? '';
      if (!invoiceNumber && invoiceId && invoiceIdToNumber.has(invoiceId)) {
        invoiceNumber = invoiceIdToNumber.get(invoiceId) || '';
      }
      const paymentStatusRaw = (order.paymentStatus ?? order.PaymentStatus ?? '').toUpperCase();
      const orderStatusRaw = (order.orderStatus ?? order.OrderStatus ?? order.status ?? order.Status ?? '').toString();

      const isCancelledOrder = ['CANCELLED', 'REJECTED', 'CANCEL', '1500', '1600', 'UNSUPPLIED'].some(s =>
        orderStatusRaw.toUpperCase().includes(s) || paymentStatusRaw.includes(s)
      );
      const isPaidOrder =
        paymentStatusRaw === 'PAID'
        || order.isPaid === true
        || order.IsPaid === true
        || (!!invoiceNumber && platform === 'trendyol');
      const hasReceipt = invoiceNumber ? prefixInvoiceNumbers.has(invoiceNumber) : undefined;
      const hasInvoice = !!((invoiceNumber && invoiceNumberSet.has(invoiceNumber)) || (invoiceId && invoiceIdSet.has(invoiceId)));

      const rawBranchId = order.branchId ?? order.BranchId;
      const branchIdNorm =
        rawBranchId != null && String(rawBranchId).trim() !== ''
          ? String(rawBranchId).trim()
          : undefined;

      const summary: PlatformOrderSummary = {
        id,
        orderNumber,
        customerName,
        totalAmount,
        createdAt,
        invoiceNumber,
        invoiceId,
        branchId: branchIdNorm,
        platform,
        hasReceipt,
        hasInvoice,
        paymentStatus: isCancelledOrder ? 'CANCELLED' : isPaidOrder ? 'PAID' : 'UNPAID',
      };

      if (isCancelledOrder) {
        cancelled.push(summary);
      } else if (isPaidOrder) {
        paid.push(summary);
        if (!invoiceNumber && !hasInvoice) {
          warnings.push({ ...summary, hasReceipt: false, hasInvoice: false });
        } else if (invoiceNumber && !hasInvoice) {
          warnings.push({ ...summary });
        } else if (invoiceNumber && !hasReceipt) {
          warnings.push({ ...summary });
        }
      } else {
        unpaid.push(summary);
      }
    }

    return {
      paid, unpaid, cancelled, warnings,
      paidTotal: paid.reduce((s, o) => s + o.totalAmount, 0),
      unpaidTotal: unpaid.reduce((s, o) => s + o.totalAmount, 0),
      cancelledTotal: cancelled.reduce((s, o) => s + o.totalAmount, 0),
      warningTotal: warnings.reduce((s, o) => s + o.totalAmount, 0),
    };
  };

  const reconcileSimple = (rows: any[], platform: string): ReconciliationResult => {
    const paid: PlatformOrderSummary[] = [];
    const cancelled: PlatformOrderSummary[] = [];
    const warnings: PlatformOrderSummary[] = [];

    for (const row of rows) {
      let invNum = row.invoiceNumber ?? '';
      const invId = row.invoiceId ?? '';
      const hasInvoice = (invNum && invoiceNumberSet.has(invNum)) || (invId && invoiceIdSet.has(invId));

      if (!invNum && invId && invoiceIdToNumber.has(invId)) {
        invNum = invoiceIdToNumber.get(invId) || '';
      }

      const summary: PlatformOrderSummary = {
        id: String(row.id ?? ''),
        orderNumber: row.orderNumber ?? row.referenceNumber ?? row.receiptNumber ?? '',
        customerName: row.customerName ?? '-',
        totalAmount: row.grandTotal ?? 0,
        createdAt: row.openedAt ?? row.transactionDate ?? row.createdAt ?? '',
        invoiceNumber: invNum,
        platform,
        paymentStatus: row.status === 'CANCELLED' ? 'CANCELLED' : 'PAID',
        hasReceipt: true,
        hasInvoice: !!hasInvoice,
        invoiceId: invId,
      };
      if (row.status === 'CANCELLED') {
        cancelled.push(summary);
      } else {
        paid.push(summary);
        if (!hasInvoice) {
          warnings.push({ ...summary });
        }
      }
    }

    return {
      paid, unpaid: [], cancelled, warnings,
      paidTotal: paid.reduce((s, o) => s + o.totalAmount, 0),
      unpaidTotal: 0,
      cancelledTotal: cancelled.reduce((s, o) => s + o.totalAmount, 0),
      warningTotal: warnings.reduce((s, o) => s + o.totalAmount, 0),
    };
  };

  const newReconciliation: Record<string, ReconciliationResult> = {
    quickSale: reconcileSimple(processedQuickSales, 'quickSale'),
    restaurant: reconcileSimple(restaurantSales.map(s => ({
      id: s.Id, orderNumber: s.SaleNumber, customerName: s.InvoiceNumber ? 'Genel Cari' : '',
      grandTotal: s.TotalAmount, openedAt: s.OpenedAt, invoiceNumber: s.InvoiceNumber,
      invoiceId: (s as any).InvoiceId || '',
      status: (s.PaymentMethod?.toLowerCase() === 'unpaid' || s.PaymentMethod?.toLowerCase() === 'cancelled' || s.PaymentMethod?.toLowerCase() === 'iptal') ? 'CANCELLED' : 'PAID',
    })), 'restaurant'),
    kiosk: reconcileSimple(processedKioskData, 'kiosk'),
    qrMenu: emptyReconciliation,
    package: emptyReconciliation,
    getir: emptyReconciliation,
    migros: emptyReconciliation,
    yemeksepeti: emptyReconciliation,
    trendyol: emptyReconciliation,
  };

  const extractData = (result: PromiseSettledResult<any>) =>
    result.status === 'fulfilled' && result.value && (result.value.Success || result.value.success)
      ? filterByDate(result.value.Data || result.value.data || [])
      : [];

  newReconciliation.package = reconcile(extractData(pkgPlatform).filter((o: any) => {
    const src = (o.syncSource ?? o.SyncSource ?? '').toLowerCase();
    return src === 'phone' || src === 'manual' || src === '' || src === 'web' || src === 'ecommerce';
  }), 'package', 'PKT-');
  newReconciliation.getir = reconcile(extractData(getirPlatform), 'getir', 'GTR-');
  newReconciliation.migros = reconcile(extractData(migrosPlatform), 'migros', 'MGR-', 100);
  newReconciliation.yemeksepeti = reconcile(extractData(ysPlatform), 'yemeksepeti', 'YS-');
  newReconciliation.trendyol = reconcile(extractData(trendyolPlatform), 'trendyol', 'TRD-');

  // Müşteri adlarını platform verilerinden güncelle
  const updateCustomerNamesFromPlatform = (
    orders: EndOfDayPackageOrderRow[],
    platformResult: PromiseSettledResult<any>
  ): EndOfDayPackageOrderRow[] => {
    const platformOrders = extractData(platformResult);
    if (platformOrders.length === 0 || orders.length === 0) return orders;

    const normRef = (s: string) => s.trim();

    const customerByInvoiceId = new Map<string, string>();
    const customerByInvoiceNum = new Map<string, string>();
    const customerByOrderRef = new Map<string, string>();
    for (const order of platformOrders) {
      const name = extractCustomerName(order);
      if (!name) continue;
      const invId = String(order.invoiceId ?? order.InvoiceId ?? '');
      const invNum = String(order.invoiceNumber ?? order.InvoiceNumber ?? '');
      if (invId) customerByInvoiceId.set(invId, name);
      if (invNum) customerByInvoiceNum.set(invNum, name);

      const refCandidates = [
        order.orderNumber,
        order.OrderNumber,
        order.orderCode,
        order.OrderCode,
        order.packageId,
        order.PackageId,
      ].filter(Boolean).map((x: any) => String(x));
      for (const c of refCandidates) {
        const n = normRef(c);
        if (!n) continue;
        customerByOrderRef.set(n, name);
        customerByOrderRef.set(n.replace(/^TRD-/i, ''), name);
      }
    }

    if (
      customerByInvoiceId.size === 0 &&
      customerByInvoiceNum.size === 0 &&
      customerByOrderRef.size === 0
    ) return orders;

    let updated = false;
    const updatedOrders = orders.map(row => {
      const ordRef = row.orderNumber ? normRef(row.orderNumber) : '';
      const ordRefNoPrefix = ordRef.replace(/^TRD-/i, '');
      const platformName =
        (row.invoiceId && customerByInvoiceId.get(row.invoiceId))
        || (row.invoiceNumber && customerByInvoiceNum.get(row.invoiceNumber))
        || (ordRef && customerByOrderRef.get(ordRef))
        || (ordRefNoPrefix && customerByOrderRef.get(ordRefNoPrefix))
        || '';
      if (platformName) {
        updated = true;
        return { ...row, customerName: platformName };
      }
      return row;
    });

    return updated ? updatedOrders : orders;
  };

  processedGetirOrders = updateCustomerNamesFromPlatform(processedGetirOrders, getirPlatform);
  processedMigrosOrders = updateCustomerNamesFromPlatform(processedMigrosOrders, migrosPlatform);
  processedYsOrders = updateCustomerNamesFromPlatform(processedYsOrders, ysPlatform);
  processedTrendyolOrders = updateCustomerNamesFromPlatform(processedTrendyolOrders, trendyolPlatform);

  // Şube seçili raporda BranchId bilinmeyen ödenmemiş platform satırlarını filtrele
  const filterPlatformUnpaidBySelectedBranches = (unpaid: PlatformOrderSummary[]): PlatformOrderSummary[] => {
    if (isAllBranches || unpaid.length === 0) return unpaid;
    return unpaid.filter(u => {
      const bid = (u.branchId || '').trim();
      return bid !== '' && selectedBranches.includes(bid);
    });
  };

  // Platform siparişlerini tab verilerine birleştir
  // bucket: 'unpaid' | 'paid-no-receipt' | 'cancelled'
  type MergeBucket = 'unpaid' | 'paid-no-receipt' | 'cancelled';
  const convertPlatformOrderToRow = (
    order: PlatformOrderSummary,
    bucket: MergeBucket
  ): EndOfDayPackageOrderRow => {
    const isCancelled = bucket === 'cancelled';
    return {
      id: `${bucket}-${order.platform}-${order.id}`,
      rowNumber: 0,
      orderNumber: order.orderNumber,
      shortCode: '',
      status: (isCancelled ? 'CANCELLED' : 'UNPAID') as TransactionStatus,
      orderStatus: isCancelled ? 'CANCELLED' : 'DELIVERED',
      subtotal: order.totalAmount,
      grandTotal: order.totalAmount,
      paidAmount: 0,
      paymentType: 'NONE' as PaymentType,
      paymentAccountName: isCancelled
        ? 'İptal'
        : bucket === 'paid-no-receipt'
          ? 'Bilinmiyor (Platform Ödendi)'
          : 'Bilinmiyor',
      cancelledTotal: isCancelled ? order.totalAmount : 0,
      createdAt: order.createdAt,
      paymentDate: null,
      customerName: order.customerName || '-',
      customerPhone: '',
      customerAddress: '',
      courierName: '',
      invoiceNumber: '',
      branchId: order.branchId,
      branchName: order.branchId ? getBranchName(order.branchId) : '-',
    };
  };

  // Geriye uyumluluk için isim koruyoruz; tüm bucket'ları (unpaid + paid-no-receipt + cancelled) birleştirir.
  const mergeUnpaidIntoData = (
    existing: EndOfDayPackageOrderRow[],
    unpaid: PlatformOrderSummary[],
    paidNoReceipt: PlatformOrderSummary[] = [],
    cancelled: PlatformOrderSummary[] = []
  ): EndOfDayPackageOrderRow[] => {
    if (unpaid.length === 0 && paidNoReceipt.length === 0 && cancelled.length === 0) return existing;

    const existingIds = new Set(existing.map(r => r.id));
    const existingInvoiceNumbers = new Set(existing.map(r => r.invoiceNumber).filter(Boolean));

    const filterNotInReceipts = (list: PlatformOrderSummary[]) => list.filter(u => {
      if (u.invoiceNumber && existingInvoiceNumbers.has(u.invoiceNumber)) return false;
      if (u.invoiceId && existingInvoiceNumbers.has(u.invoiceId)) return false;
      return true;
    });

    const filteredUnpaid = filterNotInReceipts(unpaid);
    const filteredPaidNoReceipt = filterNotInReceipts(paidNoReceipt);
    const filteredCancelled = filterNotInReceipts(cancelled);

    if (filteredUnpaid.length === 0 && filteredPaidNoReceipt.length === 0 && filteredCancelled.length === 0) {
      return existing;
    }

    const newRows = [
      ...filteredUnpaid.map(o => convertPlatformOrderToRow(o, 'unpaid')),
      ...filteredPaidNoReceipt.map(o => convertPlatformOrderToRow(o, 'paid-no-receipt')),
      ...filteredCancelled.map(o => convertPlatformOrderToRow(o, 'cancelled')),
    ].filter(r => !existingIds.has(r.id));

    if (newRows.length === 0) return existing;
    const merged = [...existing, ...newRows];
    return merged.map((row, idx) => ({ ...row, rowNumber: idx + 1 }));
  };

  const mergedPackage = mergeUnpaidIntoData(
    processedPackageOrders,
    filterPlatformUnpaidBySelectedBranches(newReconciliation.package.unpaid)
  );
  const mergedGetir = mergeUnpaidIntoData(
    processedGetirOrders,
    filterPlatformUnpaidBySelectedBranches(newReconciliation.getir.unpaid)
  );
  const mergedMigros = mergeUnpaidIntoData(
    processedMigrosOrders,
    filterPlatformUnpaidBySelectedBranches(newReconciliation.migros.unpaid)
  );
  // Yemeksepeti: platform "PAID" diyor ama bizde fatura yok → "Bilinmiyor (Platform Ödendi)" satırı eklenir.
  // İptal edilen YS siparişleri de tab'a eklenir. Sadece YS için bu davranış aktif.
  const mergedYs = mergeUnpaidIntoData(
    processedYsOrders,
    filterPlatformUnpaidBySelectedBranches(newReconciliation.yemeksepeti.unpaid),
    filterPlatformUnpaidBySelectedBranches(newReconciliation.yemeksepeti.paid),
    filterPlatformUnpaidBySelectedBranches(newReconciliation.yemeksepeti.cancelled)
  );
  // Trendyol ödenmemişleri diğer platformlarla (Getir/YS/Migros/Paket) simetrik şekilde filtrele.
  // `filterPlatformUnpaidBySelectedBranches` BranchId boş veya seçili şubelerde olmayan
  // hayalet kayıtları zaten eliyor — `isAllBranches` özel-case'i gerekmiyor.
  const mergedTrendyol = mergeUnpaidIntoData(
    processedTrendyolOrders,
    filterPlatformUnpaidBySelectedBranches(newReconciliation.trendyol.unpaid)
  );

  // Eski format summary hesapla (handlePrintReceipt için)
  const summary = calculateCombinedSummaryOld(
    restaurantSales, processedQuickSales, processedKioskData, processedQRMenuData,
    mergedPackage, mergedGetir, mergedMigros, mergedYs, mergedTrendyol
  );

  return {
    restaurantData: processedRestaurantData,
    quickSaleData: processedQuickSales,
    kioskData: processedKioskData,
    qrMenuData: processedQRMenuData,
    packageOrderData: mergedPackage,
    getirOrderData: mergedGetir,
    migrosOrderData: mergedMigros,
    ysOrderData: mergedYs,
    trendyolOrderData: mergedTrendyol,
    reconciliationData: newReconciliation,
    summary,
    completedSales: restaurantSales,
    activeTables: allActiveSales,
    screenOptions,
  };
}

// ============================================================
// HELPER: ÖKC filtresi (filterFiscalOnly = false → ÖKC'siz nakit gizle)
// EndOfDayReport.tsx satır 2715-2741 ile birebir aynı.
// ============================================================

function applyFiscalFilter(dataset: EndOfDayDataset, filterFiscalOnly: boolean): {
  filteredRestaurantData: EndOfDayRestaurantRow[];
  filteredQuickSaleData: EndOfDayQuickSaleRow[];
  nonFiscalCashTotal: number;
} {
  const shouldHideNonFiscal = !filterFiscalOnly;

  const filteredRestaurantData = !shouldHideNonFiscal
    ? dataset.restaurantData
    : dataset.restaurantData
        .filter(row => !(row.paymentType === 'CASH' && row.isFiscalReceipt === false))
        .map((row, i) => ({ ...row, rowNumber: i + 1 }));

  const filteredQuickSaleData = !shouldHideNonFiscal
    ? dataset.quickSaleData
    : dataset.quickSaleData
        .filter(row => !(row.paymentType === 'CASH' && row.isFiscalReceipt === false))
        .map((row, i) => ({ ...row, rowNumber: i + 1 }));

  let nonFiscalCashTotal = 0;
  if (shouldHideNonFiscal) {
    const fromRestaurant = dataset.restaurantData
      .filter(row => row.paymentType === 'CASH' && row.isFiscalReceipt === false && row.status !== 'CANCELLED')
      .reduce((sum, row) => sum + row.paidAmount, 0);
    const fromQuickSale = dataset.quickSaleData
      .filter(row => row.paymentType === 'CASH' && row.isFiscalReceipt === false && row.status !== 'CANCELLED')
      .reduce((sum, row) => sum + row.paidAmount, 0);
    nonFiscalCashTotal = fromRestaurant + fromQuickSale;
  }

  return { filteredRestaurantData, filteredQuickSaleData, nonFiscalCashTotal };
}

// ============================================================
// MAIN: buildEndOfDayPrintData
// EndOfDayReport.tsx handlePrintReceipt (2841-3334) ile birebir aynı.
// `printEndOfDayReceipt` ÇAĞRISINI YAPMAZ — sadece veri üretir.
// ============================================================

export async function buildEndOfDayPrintData(
  filters: EndOfDayPrintFilters,
  dataset: EndOfDayDataset,
  ctx: EndOfDayServiceContext
): Promise<EndOfDayPrintData> {
  const { startDate, endDate, branchIds: selectedBranches, filterFiscalOnly } = filters;
  const { branches, getBranchName, isManager } = ctx;

  const isAllBranches = selectedBranches.length === 0;

  const getSelectedBranchLabel = (): string => {
    if (isAllBranches) return 'Tüm Şubeler';
    return selectedBranches.map(id => getBranchName(id)).filter(Boolean).join(', ');
  };

  // ÖKC filtresini uygula
  const { filteredRestaurantData, filteredQuickSaleData, nonFiscalCashTotal } = applyFiscalFilter(dataset, filterFiscalOnly);

  const {
    kioskData,
    qrMenuData,
    packageOrderData,
    getirOrderData,
    migrosOrderData,
    ysOrderData,
    trendyolOrderData,
    reconciliationData,
    summary,
    completedSales,
  } = dataset;

  // Bu değişken `isManager` `branches` başvurularını kullanmadığı durum için TS6133 uyarısını engeller
  void branches;
  void isManager;

  // Filtrelenen fişlerin invoiceId'lerini topla
  const invoiceIds: string[] = [];

  filteredQuickSaleData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  filteredRestaurantData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  kioskData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  qrMenuData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  packageOrderData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  getirOrderData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') {
      invoiceIds.push(row.invoiceId);
    }
  });
  migrosOrderData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') invoiceIds.push(row.invoiceId);
  });
  ysOrderData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') invoiceIds.push(row.invoiceId);
  });
  trendyolOrderData.forEach(row => {
    if (row.invoiceId && row.status !== 'CANCELLED') invoiceIds.push(row.invoiceId);
  });

  // Kasiyer isimlerini topla
  const cashierSet = new Set<string>();
  filteredQuickSaleData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
    if (row.createdByName && row.createdByName !== row.cashierName) cashierSet.add(row.createdByName);
  });
  filteredRestaurantData.forEach(row => {
    if (row.waiterName) cashierSet.add(row.waiterName);
  });
  packageOrderData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
  });
  getirOrderData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
  });
  migrosOrderData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
  });
  ysOrderData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
  });
  trendyolOrderData.forEach(row => {
    if (row.cashierName) cashierSet.add(row.cashierName);
  });

  // Ödeme özeti
  let cashTotal = 0;
  let posTotal = 0;
  let prepaidTotal = 0; // PYK ön ödemeli (kart) — kasa/banka değil, ayrı gösterilir
  const onlinePaymentsMap = new Map<string, number>();
  const otherPaymentsMap = new Map<string, number>();

  filteredQuickSaleData.forEach(row => {
    if (row.status === 'CANCELLED') return;

    if (row.payments && row.payments.length > 0) {
      row.payments.forEach(payment => {
        const method = payment.paymentMethod?.toUpperCase() || '';
        const providerName = payment.paymentProviderName?.toUpperCase() || '';
        const providerType = payment.paymentProviderType?.toUpperCase() || '';
        const amount = payment.amount || 0;

        const isOnlinePayment = method.startsWith('ONLINE_') || providerType.startsWith('ONLINE_');

        let onlinePaymentType = '';
        if (providerType.endsWith('_NAKİT') || providerType.endsWith('_NAKIT')) {
          onlinePaymentType = 'Nakit';
        } else if (providerType.endsWith('_POS')) {
          onlinePaymentType = 'POS';
        } else if (providerType.endsWith('_ONLİNE') || providerType.endsWith('_ONLINE')) {
          onlinePaymentType = 'Online';
        }

        const isOnlinePlatform = providerName.includes('YEMEK') ||
                                 providerName.includes('GETİR') ||
                                 providerName.includes('TRENDYOL') ||
                                 providerName.includes('UBER') ||
                                 providerName.includes('MİGROS');

        const isOtherPayment = providerName.includes('MULTİNET') ||
                               providerName.includes('MULTINET') ||
                               providerName.includes('PLUXEE') ||
                               providerName.includes('TICKET') ||
                               providerName.includes('EDENRED') ||
                               providerName.includes('SETCARD') ||
                               providerName.includes('TOKENFLEX') ||
                               providerName.includes('METROPOL') ||
                               method === 'OTHER';

        if (method === 'PREPAID') {
          prepaidTotal += amount;
        } else if (isOnlinePlatform || isOnlinePayment) {
          let paymentTypeSuffix = '';
          if (onlinePaymentType) {
            paymentTypeSuffix = ` (${onlinePaymentType})`;
          } else if (method === 'ONLINE_CASH' || method === 'CASH' || method === 'NAKİT') {
            paymentTypeSuffix = ' (Nakit)';
          } else if (method === 'ONLINE_POS' || method === 'POS' || method === 'CREDIT_CARD') {
            paymentTypeSuffix = ' (POS)';
          } else if (method === 'ONLINE_ONLINE' || method === 'ONLINE') {
            paymentTypeSuffix = ' (Online)';
          } else {
            paymentTypeSuffix = ' (Nakit)';
          }

          const key = providerName + paymentTypeSuffix;
          const current = onlinePaymentsMap.get(key) || 0;
          onlinePaymentsMap.set(key, current + amount);
        } else if (method === 'OPEN_ACCOUNT') {
          otherPaymentsMap.set('AÇIK HESAP', (otherPaymentsMap.get('AÇIK HESAP') || 0) + amount);
        } else if (isOtherPayment || (providerName && !isOnlinePlatform)) {
          const key = providerName || payment.cashboxName || 'Diğer';
          const current = otherPaymentsMap.get(key) || 0;
          otherPaymentsMap.set(key, current + amount);
        } else if (method === 'CASH' || method === 'NAKİT') {
          cashTotal += amount;
        } else if (method === 'POS' || method === 'CREDIT_CARD') {
          posTotal += amount;
        } else {
          cashTotal += amount;
        }
      });
    } else {
      if (row.paymentType === 'PREPAID') {
        prepaidTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'OPEN_ACCOUNT') {
        otherPaymentsMap.set('AÇIK HESAP', (otherPaymentsMap.get('AÇIK HESAP') || 0) + (row.paidAmount || 0));
      } else if (row.paymentType === 'OTHER') {
        const providerName = row.paymentProviderName?.toUpperCase() || 'DİĞER';
        otherPaymentsMap.set(providerName, (otherPaymentsMap.get(providerName) || 0) + (row.paidAmount || 0));
      } else if (row.paymentType === 'ONLINE') {
        const providerName = row.paymentProviderName?.toUpperCase() || 'ONLINE';
        const key = `${providerName} (Online)`;
        const current = onlinePaymentsMap.get(key) || 0;
        onlinePaymentsMap.set(key, current + (row.paidAmount || 0));
      } else if (row.paymentType === 'CASH') {
        cashTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'POS') {
        posTotal += row.paidAmount || 0;
      }
    }
  });

  filteredRestaurantData.forEach(row => {
    if (row.status === 'CANCELLED') return;

    const sale = completedSales.find(s => s.Id === Number(row.id));
    if (sale?.Payments && sale.Payments.length > 0) {
      sale.Payments.forEach(payment => {
        const method = payment.PaymentMethod?.toLowerCase() || '';
        if (method === 'cash' || method === 'nakit') {
          cashTotal += payment.Amount || 0;
        } else if (method === 'pos' || method === 'credit_card') {
          posTotal += payment.Amount || 0;
        } else if (method === 'open_account' || method === 'açık hesap') {
          otherPaymentsMap.set('AÇIK HESAP', (otherPaymentsMap.get('AÇIK HESAP') || 0) + (payment.Amount || 0));
        } else {
          cashTotal += payment.Amount || 0;
        }
      });
    } else {
      if (row.paymentType === 'OPEN_ACCOUNT') {
        otherPaymentsMap.set('AÇIK HESAP', (otherPaymentsMap.get('AÇIK HESAP') || 0) + (row.paidAmount || 0));
      } else if (row.paymentType === 'CASH') {
        cashTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'POS') {
        posTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'MIXED') {
        cashTotal += (row.paidAmount || 0) / 2;
        posTotal += (row.paidAmount || 0) / 2;
      }
    }
  });

  // Paket Sipariş ödemeleri
  let packageCashTotal = 0;
  let packagePosTotal = 0;

  packageOrderData.forEach(row => {
    if (row.status === 'CANCELLED') return;

    if (row.paymentType === 'OTHER' && row.otherAmount && row.otherAmount > 0) {
      const label = row.paymentProviderName?.toUpperCase() || 'DİĞER';
      const key = `PAKET ${label}`;
      otherPaymentsMap.set(key, (otherPaymentsMap.get(key) || 0) + row.otherAmount);
      return;
    }

    if (row.cashAmount && row.cashAmount > 0) {
      packageCashTotal += row.cashAmount;
    }
    if (row.posAmount && row.posAmount > 0) {
      packagePosTotal += row.posAmount;
    }

    if (!row.cashAmount && !row.posAmount) {
      if (row.paymentType === 'CASH') {
        packageCashTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'POS') {
        packagePosTotal += row.paidAmount || 0;
      } else if (row.paymentType === 'MIXED') {
        packageCashTotal += (row.paidAmount || 0) / 2;
        packagePosTotal += (row.paidAmount || 0) / 2;
      }
    }
  });

  if (packageCashTotal > 0) {
    otherPaymentsMap.set('PAKET NAKİT', (otherPaymentsMap.get('PAKET NAKİT') || 0) + packageCashTotal);
  }
  if (packagePosTotal > 0) {
    otherPaymentsMap.set('PAKET POS', (otherPaymentsMap.get('PAKET POS') || 0) + packagePosTotal);
  }

  const processPlatformPayments = (
    data: EndOfDayPackageOrderRow[],
    platformLabel: string
  ) => {
    let plCash = 0, plPos = 0, plOnline = 0;
    const plOtherMap = new Map<string, number>();

    data.forEach(row => {
      if (row.status === 'CANCELLED') return;
      const amount = row.paidAmount || 0;

      if (row.paymentType === 'OTHER' && row.otherAmount && row.otherAmount > 0) {
        const label = row.paymentProviderName?.toUpperCase() || 'DİĞER';
        plOtherMap.set(label, (plOtherMap.get(label) || 0) + row.otherAmount);
      } else if (row.paymentType === 'ONLINE') {
        plOnline += amount;
      } else if (row.cashAmount && row.cashAmount > 0) {
        plCash += row.cashAmount;
        if (row.posAmount && row.posAmount > 0) plPos += row.posAmount;
      } else if (row.posAmount && row.posAmount > 0) {
        plPos += row.posAmount;
      } else if (row.paymentType === 'CASH') {
        plCash += amount;
      } else if (row.paymentType === 'POS') {
        plPos += amount;
      } else if (row.paymentType === 'MIXED') {
        plCash += amount / 2;
        plPos += amount / 2;
      }
    });

    if (plCash > 0) otherPaymentsMap.set(`${platformLabel} NAKİT`, (otherPaymentsMap.get(`${platformLabel} NAKİT`) || 0) + plCash);
    if (plPos > 0) otherPaymentsMap.set(`${platformLabel} POS`, (otherPaymentsMap.get(`${platformLabel} POS`) || 0) + plPos);
    if (plOnline > 0) otherPaymentsMap.set(`${platformLabel} ONLİNE`, (otherPaymentsMap.get(`${platformLabel} ONLİNE`) || 0) + plOnline);
    plOtherMap.forEach((amt, label) => {
      const key = `${platformLabel} ${label}`;
      otherPaymentsMap.set(key, (otherPaymentsMap.get(key) || 0) + amt);
    });
  };

  processPlatformPayments(getirOrderData, 'GETİR');
  processPlatformPayments(migrosOrderData, 'MİGROS');
  processPlatformPayments(ysOrderData, 'Y.SEPETİ');
  processPlatformPayments(trendyolOrderData, 'TRENDYOL');

  // Kiosk POS
  let kioskPosTotal = 0;
  kioskData.forEach(row => {
    if (row.status === 'CANCELLED') return;
    kioskPosTotal += row.paidAmount || 0;
  });
  if (kioskPosTotal > 0) {
    otherPaymentsMap.set('KIOSK POS', (otherPaymentsMap.get('KIOSK POS') || 0) + kioskPosTotal);
  }

  // Müşteri sayısı
  const customerCount = filteredRestaurantData.length + filteredQuickSaleData.length + kioskData.length + qrMenuData.length + packageOrderData.length + getirOrderData.length + migrosOrderData.length + ysOrderData.length + trendyolOrderData.length;

  // Restoran InvoiceNumber'ları
  const restaurantInvoiceNumbers: string[] = [];
  filteredRestaurantData.forEach(row => {
    if (row.status === 'CANCELLED') return;
    const sale = completedSales.find(s => s.Id === Number(row.id));
    if (sale?.InvoiceNumber) {
      restaurantInvoiceNumbers.push(sale.InvoiceNumber);
    }
  });

  // Ürün satış özetleri
  const [quickSaleProductResponse, restaurantProductResponse] = await Promise.all([
    getProductSalesSummaryByInvoiceIds(invoiceIds),
    getProductSalesSummaryByInvoiceNumbers(restaurantInvoiceNumbers),
  ]);

  const mergedProductMap = new Map<string, { unitPrice: number; totalQty: number; totalAmount: number }>();
  const addToMap = (items: ProductSalesSummaryItem[]) => {
    items.forEach(item => {
      const key = `${item.ProductName}__${item.UnitPrice}`;
      const existing = mergedProductMap.get(key);
      if (existing) {
        existing.totalQty += item.TotalQuantity;
        existing.totalAmount += item.TotalAmount;
      } else {
        mergedProductMap.set(key, {
          unitPrice: item.UnitPrice,
          totalQty: item.TotalQuantity,
          totalAmount: item.TotalAmount,
        });
      }
    });
  };
  addToMap(quickSaleProductResponse.Items || []);
  addToMap(restaurantProductResponse.Items || []);

  const productSalesItems: ProductSalesSummaryItem[] =
    Array.from(mergedProductMap.entries()).map(([key, val]) => ({
      ProductName: key.split('__')[0],
      UnitPrice: val.unitPrice,
      TotalQuantity: val.totalQty,
      TotalAmount: val.totalAmount,
    })).sort((a, b) => b.TotalAmount - a.TotalAmount);

  // Platform sipariş sayıları ve toplamları
  // total         = net toplam (indirim sonrası, grandTotal)
  // originalTotal = brüt toplam (indirim öncesi). Row'da originalTotal yoksa
  //                 (eski/iptal/eşleşmeyen kayıt) grandTotal'e düşer.
  const calcPlatform = (data: EndOfDayPackageOrderRow[]) => {
    let count = 0, total = 0, originalTotal = 0;
    data.forEach(r => {
      if (r.status !== 'CANCELLED') {
        count++;
        total += r.grandTotal || 0;
        originalTotal += r.originalTotal ?? r.grandTotal ?? 0;
      }
    });
    return { count, total, originalTotal };
  };

  const otherMethodNames = ['MULTİNET', 'MULTINET', 'PLUXEE', 'TICKET', 'EDENRED', 'SETCARD', 'TOKENFLEX', 'METROPOL', 'PAYE'];
  const extractMethodName = (key: string): string | null => {
    const upper = key.toUpperCase();
    for (const method of otherMethodNames) {
      if (upper.includes(method)) return method === 'MULTİNET' ? 'MULTINET' : method;
    }
    return null;
  };

  const otherMethodTotalsMap = new Map<string, number>();
  const addToMethodTotals = (amount: number, key: string) => {
    const method = extractMethodName(key);
    if (method) {
      otherMethodTotalsMap.set(method, (otherMethodTotalsMap.get(method) || 0) + amount);
    }
  };
  otherPaymentsMap.forEach((amount, key) => addToMethodTotals(amount, key));
  onlinePaymentsMap.forEach((amount, key) => addToMethodTotals(amount, key));

  // İptal edilen adisyonlar
  const cancelledOrders: Array<{ tableName: string; orderNumber: string; amount: number; waiterName?: string; notes?: string; closedAt?: string; source?: 'restaurant' | 'quickSale' }> = [];
  filteredRestaurantData.forEach(row => {
    if (row.status === 'CANCELLED') {
      cancelledOrders.push({
        tableName: row.tableName,
        orderNumber: row.orderNumber,
        amount: row.grandTotal || 0,
        waiterName: row.waiterName,
        notes: row.notes,
        closedAt: row.closedAt ? (typeof row.closedAt === 'string' ? row.closedAt : (row.closedAt as Date).toISOString()) : undefined,
        source: 'restaurant',
      });
    }
  });
  filteredQuickSaleData.forEach(row => {
    if (row.status === 'CANCELLED') {
      cancelledOrders.push({
        tableName: row.receiptNumber || row.referenceNumber || '-',
        orderNumber: row.referenceNumber || '',
        amount: row.grandTotal || 0,
        waiterName: row.cashierName || row.createdByName,
        notes: row.notes,
        closedAt: row.transactionDate ? (typeof row.transactionDate === 'string' ? row.transactionDate : (row.transactionDate as Date).toISOString()) : undefined,
        source: 'quickSale',
      });
    }
  });

  // Print data hazırla
  const printData: EndOfDayPrintData = {
    branchName: getSelectedBranchLabel(),
    cashierNames: Array.from(cashierSet),
    reportStartDate: startDate.toISOString(),
    reportEndDate: endDate.toISOString(),
    printDate: new Date().toISOString(),
    productSales: productSalesItems,
    totalRevenue: productSalesItems.reduce((sum, item) => sum + item.TotalAmount, 0),
    customerCount,
    paymentSummary: {
      cashTotal,
      posTotal,
      onlinePayments: Array.from(onlinePaymentsMap.entries()).map(([name, amount]) => ({ name, amount })),
      otherPayments: Array.from(otherPaymentsMap.entries()).map(([name, amount]) => ({ name, amount })),
    },
    grandTotal: (summary?.totalAmount || 0) - nonFiscalCashTotal,
    cancelledTotal: summary?.cancelledAmount || 0,
    cancelledOrders: cancelledOrders.length > 0 ? cancelledOrders : undefined,
    platformOrders: {
      getir: calcPlatform(getirOrderData),
      trendyol: calcPlatform(trendyolOrderData),
      yemeksepeti: calcPlatform(ysOrderData),
      migros: calcPlatform(migrosOrderData),
    },
    otherMethodTotals: Array.from(otherMethodTotalsMap.entries()).map(([name, amount]) => ({ name, amount })),
    posRefundTotal: summary?.posRefundAmount || 0,
    posRefundCount: summary?.posRefundCount || 0,
    prepaidTotal,
    prepaidCount: summary?.prepaidCount || 0,
    unpaidPlatformOrders: {
      package: { count: reconciliationData.package.unpaid.length, total: reconciliationData.package.unpaidTotal },
      getir: { count: reconciliationData.getir.unpaid.length, total: reconciliationData.getir.unpaidTotal },
      trendyol: { count: reconciliationData.trendyol.unpaid.length, total: reconciliationData.trendyol.unpaidTotal },
      yemeksepeti: { count: reconciliationData.yemeksepeti.unpaid.length, total: reconciliationData.yemeksepeti.unpaidTotal },
      migros: { count: reconciliationData.migros.unpaid.length, total: reconciliationData.migros.unpaidTotal },
    },
  };

  return printData;
}

// ============================================================
// CONVENIENCE: Tek seferde — veri çek + printData oluştur + yazdır
// Yeni modal bunu çağırır.
// ============================================================

export async function generateAndPrintEndOfDay(
  filters: EndOfDayPrintFilters,
  ctx: EndOfDayServiceContext
): Promise<void> {
  const dataset = await fetchEndOfDayDataset(filters, ctx);
  const printData = await buildEndOfDayPrintData(filters, dataset, ctx);
  printEndOfDayReceipt(printData);
}
